'use client';

import { useEffect, useState, type ReactNode } from 'react';
import { useInView } from 'react-intersection-observer';

export interface FadeInProps {
  children: ReactNode;
  /** Viewport-top distance (px) at which the content appears. */
  offset?: number;
}

/**
 * Collapsed until its top is within `offset` of the viewport top, then fades in and stays.
 * Source: NerdWallet/structured-content shared-core/src/components/fade-container. The observer
 * also reports on mount (seo-pages FadeContainerLogic), so content already in range shows at once.
 */
export function FadeIn({ children, offset = 200 }: FadeInProps) {
  // The root extends far above the viewport and ends `offset` px below its top, so "in view" means
  // the element's top is at or above `offset`, including after scrolling past it. The bottom margin
  // depends on the viewport height, so it is tracked on resize; observation waits until it is known.
  const [viewportHeight, setViewportHeight] = useState<number>();
  useEffect(() => {
    const update = () => setViewportHeight(window.innerHeight);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  const { ref, inView } = useInView({
    triggerOnce: true,
    skip: viewportHeight === undefined,
    rootMargin:
      viewportHeight === undefined ? undefined : `100000px 0px ${offset - viewportHeight}px 0px`,
  });

  return (
    <div
      className={
        inView
          ? 'opacity-100 transition-opacity duration-[1500ms] ease-linear'
          : // invisible: keeps collapsed links out of the tab order and accessibility tree.
            'invisible h-0 overflow-hidden opacity-0'
      }
      ref={ref}
    >
      {children}
    </div>
  );
}
